/**
 * Wisprs bindings for the engine's first-touch attribution policy.
 *
 * The parsing, classification, and serialization rules live in
 * @engine/attribution. This adapter adds Wisprs extension attribution and
 * supplies the cookie name and first-party domains for client-safe callers.
 */
import {
  SIGNUP_ATTRIBUTION_SCOPE,
  buildAttributionFromRequest as buildEngineAttributionFromRequest,
  decodeAttrCookie as decodeEngineAttrCookie,
  isFirstPartyAttributionHostname as isEngineFirstPartyAttributionHostname,
  normalizeSignupSourceForStorage as normalizeEngineSignupSourceForStorage,
  classifyChannel as classifyEngineChannel,
  summarizeSignupSource as summarizeEngineSignupSource,
  type AcquisitionSummary as EngineAcquisitionSummary,
  type SignupSource as EngineSignupSource,
  type AttributionSourceEvidence as EngineSourceEvidence,
} from '@engine/attribution';

export type AttributionSourceEvidence = EngineSourceEvidence | 'extension-connect';
export type SignupSource = Omit<EngineSignupSource, 'sourceEvidence'> & {
  sourceEvidence?: AttributionSourceEvidence;
};
export type AcquisitionSummary = Omit<EngineAcquisitionSummary, 'sourceEvidence'> & {
  sourceEvidence: AttributionSourceEvidence | null;
};

// Keep the shared engine unchanged: this workspace owns Wisprs extension policy.
function engineSource(source: SignupSource): EngineSignupSource {
  return {
    ...source,
    sourceEvidence:
      source.sourceEvidence === 'extension-connect' ? undefined : source.sourceEvidence,
  };
}

export function attrCookieJson(source: SignupSource): string {
  return JSON.stringify({ v: 1, ...source });
}

export function encodeAttrCookie(source: SignupSource): string {
  return encodeURIComponent(attrCookieJson(source));
}

export function classifyChannel(input: Parameters<typeof classifyEngineChannel>[0]): string {
  const channel = classifyEngineChannel(input);
  const extensionUtm =
    input.utm?.utm_source?.trim().toLowerCase() === 'chrome-extension' &&
    input.utm?.utm_medium?.trim().toLowerCase() === 'extension';
  return extensionUtm &&
    !input.referrerHost &&
    !input.ref &&
    !input.cl &&
    !Object.keys(input.clickIds ?? {}).length &&
    channel === 'referral'
    ? 'extension'
    : channel;
}

export function summarizeSignupSource(
  raw: unknown,
  selfHost = 'wisprs.co',
  additionalFirstPartyHosts: readonly string[] = []
): AcquisitionSummary {
  const summary = summarizeEngineSignupSource(raw, selfHost, additionalFirstPartyHosts);
  if (
    raw &&
    typeof raw === 'object' &&
    'channel' in raw &&
    raw.channel === 'extension' &&
    'sourceEvidence' in raw &&
    raw.sourceEvidence === 'extension-connect' &&
    summary.sourceEvidence === 'no-referrer'
  ) {
    return { ...summary, sourceEvidence: 'extension-connect' };
  }
  return summary;
}

export const SIGNUP_ATTR_COOKIE = 'wisprs_attr';

export const SIGNUP_ATTR_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

export const FIRST_PARTY_ATTRIBUTION_DOMAINS = ['wisprs.co'] as const;

export function isFirstPartyAttributionHostname(
  candidate: string | null | undefined,
  selfHost = 'wisprs.co',
  additionalFirstPartyHosts: readonly string[] = []
): boolean {
  return isEngineFirstPartyAttributionHostname(
    candidate,
    selfHost,
    additionalFirstPartyHosts,
    FIRST_PARTY_ATTRIBUTION_DOMAINS
  );
}

export function normalizeSignupSourceForStorage(
  source: SignupSource,
  selfHost = 'wisprs.co'
): SignupSource {
  const normalized = normalizeEngineSignupSourceForStorage(
    engineSource(source),
    selfHost,
    FIRST_PARTY_ATTRIBUTION_DOMAINS
  );
  normalized.channel =
    classifyChannel({
      utm: normalized.utm,
      ref: normalized.ref,
      cl: normalized.cl,
      clickIds: normalized.clickIds,
      referrerHost: normalized.referrer,
    }) === 'extension'
      ? 'extension'
      : normalized.channel;
  if (
    source.channel === 'extension' &&
    source.sourceEvidence === 'extension-connect' &&
    normalized.channel === 'extension'
  ) {
    return { ...normalized, sourceEvidence: 'extension-connect' };
  }
  return normalized;
}

export function decodeAttrCookie(raw: string | undefined | null): SignupSource | null {
  const source = decodeEngineAttrCookie(raw, SIGNUP_ATTRIBUTION_SCOPE);
  if (!source || !raw) return source;
  try {
    let json = raw;
    for (let i = 0; i < 3 && !json.startsWith('{'); i++) json = decodeURIComponent(json);
    if (source.channel === 'extension' && JSON.parse(json).sourceEvidence === 'extension-connect') {
      return { ...source, sourceEvidence: 'extension-connect' };
    }
  } catch {
    /* Shared decoder already validated the cookie. */
  }
  return source;
}

export function buildAttributionFromRequest(input: {
  searchParams: URLSearchParams;
  referer: string | null;
  selfHost: string;
  additionalFirstPartyHosts?: readonly string[];
  landing: string;
  capturedAt: string;
}): SignupSource | null {
  const source = buildEngineAttributionFromRequest({
    ...input,
    additionalFirstPartyHosts: [
      ...FIRST_PARTY_ATTRIBUTION_DOMAINS,
      ...(input.additionalFirstPartyHosts ?? []),
    ],
  });
  if (!source) return source;
  const channel = classifyChannel({
    utm: source.utm,
    ref: source.ref,
    cl: source.cl,
    clickIds: source.clickIds,
    referrerHost: source.referrer,
  });
  if (channel === 'extension') return { ...source, channel };

  // Old installs have no UTM or Referer. Only accept an exact connect path
  // or a same-origin Clerk auth return target.
  let extensionConnect = input.landing === '/dashboard/extension/connect';
  if (/^\/sign-(in|up)(\/|$)/.test(input.landing)) {
    const target = input.searchParams.get('redirect_url');
    if (target) {
      try {
        const origin = new URL(`https://${input.selfHost}`);
        const url = new URL(target, origin);
        extensionConnect =
          url.origin === origin.origin && url.pathname === '/dashboard/extension/connect';
      } catch {
        /* Invalid redirects carry no attribution evidence. */
      }
    }
  }
  if (
    extensionConnect &&
    source.channel === 'direct' &&
    !source.referrer &&
    !source.ref &&
    !source.cl &&
    !source.utm &&
    !source.clickIds
  ) {
    return { ...source, channel: 'extension', sourceEvidence: 'extension-connect' };
  }
  return source;
}
